/**
 * GarminAttribution — the "Garmin [device model]" credit.
 *
 * Required, not decorative. The Garmin API brand guidelines say every view of
 * device-sourced data — dashboards, activity feeds, overview cards, detail
 * views, reports, screenshots and printouts — must carry a
 * "Garmin [device model]" attribution, placed directly beneath or adjacent to
 * the primary title, above the fold, and never buried in a tooltip, footnote
 * or expandable container. Garmin reserves the right to review for compliance,
 * with suspension of API access as the stated consequence.
 *
 * Renders nothing for data that did not come from Garmin, since the same
 * guidelines forbid showing the mark where no Garmin data is present.
 */

import { Box, Typography, useTheme } from '@mui/material';
import WatchIcon from '@mui/icons-material/Watch';
import { tokens } from '../../theme';

/**
 * Garmin sends the device as either "Forerunner 910XT" or "Garmin Fenix 8"
 * depending on the summary, and the required wording is "Garmin [model]".
 * Where the model is unknown the guidelines say to credit "Garmin".
 */
export const garminDeviceLabel = (deviceName) => {
  const name = (deviceName || '').trim();
  if (!name) return 'Garmin';
  return /^garmin\b/i.test(name) ? name : `Garmin ${name}`;
};

const GarminAttribution = ({
  source,
  deviceName,
  variant = 'inline',
  sx = {},
}) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  if (source !== 'garmin') return null;

  const label = garminDeviceLabel(deviceName);

  // Print and export surfaces must keep the attribution visible, but an icon
  // font may not survive the render, so the text carries it alone there.
  if (variant === 'print') {
    return (
      <Typography variant="caption" sx={{ color: '#444', ...sx }}>
        {label}
      </Typography>
    );
  }

  return (
    <Box display="flex" alignItems="center" gap={0.5} sx={{ mt: 0.25, ...sx }}>
      <WatchIcon sx={{ fontSize: 14, color: colors.grey[400] }} />
      <Typography variant="caption" sx={{ color: colors.grey[400], fontWeight: 600 }}>
        {label}
      </Typography>
    </Box>
  );
};

export default GarminAttribution;
